import React, { useState, useEffect } from "react";
import { Rnd } from "react-rnd";

export interface FieldData {
  id: number;
  type: string;
  name: string;
  width: number;
  height: number;
  top: number;
  left: number;
  page: number;
  content?: string;
}

interface DraggableResizableFieldProps {
  field: FieldData;
  onDrag: (x: number, y: number) => void;
  onResize: (width: number, height: number) => void;
  onDelete: () => void;
  onTextEdit?: (text: string) => void;
  editorMode?: string;
  fillMode?: boolean;
}

function DraggableResizableField({
  field,
  onDrag,
  onResize,
  onDelete,
  onTextEdit,
  editorMode,
  fillMode = false,
}: DraggableResizableFieldProps) {
  const [text, setText] = useState(field.content || "");
  const [isEditing, setIsEditing] = useState(false);

  // Sync text with field.content when it changes
  useEffect(() => {
    setText(field.content || "");
  }, [field.content]);

  const handleTextChange = (newText: string) => {
    setText(newText);
    if (onTextEdit) {
      onTextEdit(newText);
    }
  };

  const isSignatureOrStamp = ["Signature", "Tampon"].includes(field.type);
  const isImageField = text && text.startsWith("data:image");
  const isTextField = ["Text", "Date"].includes(field.type);

  return (
    <>
      <Rnd
        className="z-10"
        position={{ x: field.left, y: field.top }}
        size={{ width: field.width, height: field.height }}
        bounds="parent"
        disableDragging={fillMode}
        enableResizing={fillMode ? false : {
          top: false,
          right: true,
          bottom: true,
          left: false,
          bottomRight: true,
          bottomLeft: false,
          topRight: false,
          topLeft: false,
        }}
        onResizeStop={(e, direction, ref) => {
          const newWidth = parseInt(ref.style.width, 10);
          const newHeight = parseInt(ref.style.height, 10);
          onResize(newWidth, newHeight);
        }}
        onDragStop={(e, d) => {
          onDrag(d.x, d.y);
        }}
      >
        <div
          className="w-full h-full items-center justify-center border-2 border-black border-dashed relative flex bg-blue-300 opacity-65 rounded-lg overflow-hidden"
          style={{
            fontSize: !isSignatureOrStamp
              ? `${Math.min(field.width, field.height) * 0.4}px`
              : "14px",
            cursor: fillMode ? "pointer" : "move",
          }}
          onClick={fillMode && !isTextField ? () => {} : undefined}
        >
          {isImageField ? (
            <img src={text} alt={field.name} className="w-full h-full object-contain" />
          ) : fillMode && isTextField && isEditing ? (
            <input
              type="text"
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
              onBlur={() => setIsEditing(false)}
              autoFocus
              className="w-full h-full px-2 bg-transparent border-none outline-none text-center"
              style={{
                fontSize: `${Math.min(field.width, field.height) * 0.4}px`,
              }}
            />
          ) : fillMode && isTextField ? (
            <span
              className="truncate px-2 cursor-text"
              onClick={() => setIsEditing(true)}
            >
              {text || "Cliquez pour saisir"}
            </span>
          ) : (
            <span className="truncate px-2">{text || field.name || field.type}</span>
          )}

          {!fillMode && (
            <>
              <div
                onClick={onDelete}
                className="absolute bg-black rounded-full cursor-pointer p-1 z-20 hover:bg-red-500 -top-3 -right-3"
                style={{ width: 24, height: 24 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  width="16"
                  height="16"
                >
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </div>
              <div className="absolute bg-black rounded-full cursor-se-resize p-1 z-20 -bottom-3 -right-3" style={{ width: 24, height: 24 }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  width="16"
                  height="16"
                >
                  <path d="M22 22H20V20H22V22M22 18H20V16H22V18M18 22H16V20H18V22M18 18H16V16H18V18M14 22H12V20H14V22M22 14H20V12H22V14Z" />
                </svg>
              </div>
            </>
          )}
        </div>
      </Rnd>
    </>
  );
}

export default DraggableResizableField;
